import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { PersonReportCard } from '@/components/common/PersonReportCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { CadePersonConselhoPanels } from '@/pages/cade/conselho/PersonConselhoPanels';
import { fetchCadeConselheiro } from '@/services/cade';
import { RelatorNarrative } from '@/pages/cade/RelatorNarrative';
import { Stat } from '@/pages/cade/Stat';
import type {
  CadeConselheiroDecision,
  CadeConselheiroProfile,
  CadeLabelCount,
  CategoryShare,
} from '@/services/types';

/**
 * One CADE conselheiro -- the product's first page about a named individual.
 *
 * That is exactly why the restraints are heavier here than on any list screen. A
 * page with one person's name at the top invites the reading both blueprints
 * forbid: that the numbers below describe the person rather than the docket they
 * were handed. So:
 *
 *  - counts, never rates, and the case-type mix sits BESIDE the outcome mix
 *    rather than being divided out of it;
 *  - fines are counted, never averaged -- an average fine per rapporteur is a
 *    severity ranking wearing a statistic's clothes;
 *  - below the sample floor the outcome mix is REFUSED with `scope="record"`, so
 *    the badge says this person is thinly recorded rather than that CADE has no
 *    rapporteur data at all.
 */
export function CadeConselheiroPage() {
  const { slug = '' } = useParams();
  const { t } = useTranslation(['cade']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'conselheiro', slug],
    queryFn: () => fetchCadeConselheiro(slug),
    // A 404 is a real answer -- this slug names nobody -- so it must not be
    // retried into a slow spinner. Same shape as the case detail page.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('cade:conselheiro.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const name = data.profile.value?.name ?? slug;

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-foreground">{name}</h1>
          <p className="text-sm text-muted-foreground">{t('cade:conselheiro.subtitle')}</p>
        </div>
        {/* Parity with `/anatel/r/:slug`, which has exported since it shipped.
            Both CADE detail pages went without one. */}
        <ExportButton path={`/api/v1/cade/export/conselheiros/${encodeURIComponent(slug)}`} />
      </header>

      <PersonReportCard reports={data.conselho?.reports} />

      <ExplainerCard i18nKey="cade:conselheiro.explainer" />
      <AsOfStrip asOf={data.as_of} />

      {/* The record as cases, first: what a lawyer reads before petitioning. */}
      {data.narrative ? <RelatorNarrative narrative={data.narrative} /> : null}

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:conselheiro.record.title')}
        </h2>
        <Answer answer={data.profile} help={t('cade:conselheiro.record.help')} scope="record">
          {(person) => <ProfilePanel person={person} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:conselheiro.guide.record" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:conselheiro.outcomes.title')}
        </h2>
        {/* `scope="record"`: the corpus supports outcome mixes perfectly well.
            What is thin is this one person's record, and the corpus-scoped
            wording would say the opposite. When the person's mix answers, the
            table below carries it beside the bench's; when it refuses, this
            block says so and the bench column stands alone -- the refusal is
            about the person, and the docket the Tribunal received is not
            thinner for it. */}
        <Answer
          answer={data.outcome_mix}
          help={t('cade:conselheiro.outcomes.help')}
          scope="record"
          elsewhere={t('cade:conselheiro.outcomes.elsewhere')}
        >
          {() => null}
        </Answer>
        {data.corpus_mix.value ? (
          <div className="mt-3">
            <MixComparison
              person={data.outcome_mix.value}
              personTotal={data.profile.value?.classified ?? 0}
              bench={data.corpus_mix.value}
              benchTotal={data.corpus_mix.population.measured}
            />
          </div>
        ) : (
          <div className="mt-3">
            <Answer answer={data.corpus_mix} help={t('cade:conselheiro.comparison.help')}>
              {() => null}
            </Answer>
          </div>
        )}
        <div className="mt-3">
          <FieldGuide i18nKey="cade:conselheiro.guide.comparison" />
        </div>
      </section>

      <DecisionList items={data.items} />
      {data.conselho ? <CadePersonConselhoPanels conselho={data.conselho} /> : null}
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['cade']);
  return (
    <Link
      to="/cade/commissioners"
      className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t('cade:conselheiro.backToBench')}
    </Link>
  );
}

function ProfilePanel({ person }: { person: CadeConselheiroProfile }) {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-4 text-xs">
      {/* Presence, not a mandate: the corpus has no appointment record, so the
          line reads the first and last archive year the person appears in the
          minutes, in either role, and the sessions counted between them. */}
      {person.first_year !== null && person.last_year !== null ? (
        <p className="text-sm text-foreground" data-tenure>
          {t('cade:conselheiro.tenure', {
            window: t('common:asOf.yearWindow', { first: person.first_year, last: person.last_year }),
            sessions: number.format(person.sessions),
          })}
        </p>
      ) : null}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t('cade:conselheiro.stats.final')}
          value={`${number.format(person.final_decisions)} / ${number.format(person.decisions)}`}
        />
        <Stat
          label={t('cade:conselheiro.stats.benchShare')}
          value={percent.format(person.bench_share)}
        />
        <Stat
          label={t('cade:conselheiro.stats.withFine')}
          value={number.format(person.decisions_with_fine)}
        />
        <Stat
          label={t('cade:conselheiro.stats.votes')}
          value={number.format(person.votes)}
          hint={t('cade:conselheiro.stats.recusalsHint', { count: person.recusals })}
        />
      </dl>
      {/* Posture counts, second row deliberately: they describe how the docket
          was COMPOSED (how many TCCs and appeal matters this person was handed
          as relator), which is the confound for reading the outcome mix above
          -- not a leniency score, and the hint says so. */}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t('cade:conselheiro.stats.tcc')}
          value={`${number.format(person.tcc_homologated)} · ${percent.format(person.tcc_share)}`}
          hint={t('cade:conselheiro.stats.tccHint')}
        />
        <Stat
          label={t('cade:conselheiro.stats.appealDocket')}
          value={number.format(person.appeal_docket)}
          hint={t('cade:conselheiro.stats.appealDocketHint')}
        />
      </dl>

      {person.by_year.length > 0 ? (
        <div className="space-y-1.5">
          <h3 className="font-medium text-foreground">{t('cade:conselheiro.byYear.title')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 font-medium">{t('cade:conselheiro.byYear.year')}</th>
                  <th className="px-2 py-1 text-right font-medium">
                    {t('cade:conselheiro.byYear.decisions')}
                  </th>
                  <th className="px-2 py-1 text-right font-medium">
                    {t('cade:conselheiro.byYear.final')}
                  </th>
                  <th className="px-2 py-1 text-right font-medium">
                    {t('cade:conselheiro.byYear.withFine')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {person.by_year.map((row) => (
                  <tr key={row.year} className="border-t">
                    <td className="px-2 py-1 tabular-nums text-foreground">{row.year}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                      {number.format(row.decisions)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-foreground">
                      {number.format(row.final_decisions)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-foreground">
                      {number.format(row.with_fine)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <h3 className="font-medium text-foreground">
            {t('cade:conselheiro.caseTypes', {
              concentration: percent.format(person.concentration),
            })}
          </h3>
          <Bars
            items={person.case_types}
            total={person.final_decisions}
            emptyLabel={t('cade:conselheiro.noCaseTypes')}
          />
          {/* The same disclosure the bench table carries, from the same key: this
              page renders the identical raw labels, and `Ato de Concentração` and
              `Ato de concentração` sit as separate rows because the source
              records them that way. */}
          <p className="text-muted-foreground">{t('cade:commissioners.rawTypes')}</p>
        </div>
        <div className="space-y-1.5">
          <h3 className="font-medium text-foreground">{t('cade:conselheiro.positions')}</h3>
          {person.positions.length === 0 ? (
            <p className="text-muted-foreground">{t('cade:conselheiro.noVotes')}</p>
          ) : (
            <ul className="space-y-1">
              {person.positions.map((position) => (
                <li key={position.position} className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-muted-foreground">
                    {t(`cade:overview.positions.${position.position}`, position.position)}
                  </span>
                  <span className="shrink-0 tabular-nums text-foreground">
                    {number.format(position.votes)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A count over its denominator, never a bare percentage.
 *
 * Copied in spirit from `CommissionersPage`'s own `Bars`: the share drives the
 * width and the numbers stay legible beside it, so nobody reads a 100% bar over
 * two decisions as a finding.
 */
function Bars({
  items,
  total,
  emptyLabel,
}: {
  items: CadeLabelCount[];
  total: number;
  emptyLabel: string;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  if (items.length === 0) return <p className="text-muted-foreground">{emptyLabel}</p>;
  const shown = items.slice(0, 6);

  return (
    <ul className="space-y-1">
      {shown.map((item) => (
        <li key={item.label} className="space-y-0.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-muted-foreground">
              {t(`cade:overview.outcomes.${item.label}`, item.label)}
            </span>
            <span className="shrink-0 tabular-nums text-foreground">
              {number.format(item.decisions)} / {number.format(total)}
            </span>
          </div>
          <div className="h-1 rounded bg-muted">
            <div
              className="h-1 rounded bg-primary"
              style={{ width: total ? `${Math.round((item.decisions / total) * 100)}%` : '0%' }}
            />
          </div>
        </li>
      ))}
      {/* Said, not silently cut, as on the bench page. */}
      {items.length > shown.length ? (
        <li className="text-muted-foreground">
          {t('cade:commissioners.moreBars', { count: items.length - shown.length })}
        </li>
      ) : null}
    </ul>
  );
}

/**
 * The person's outcome mix beside the bench's, as `n / N (p%)` cells.
 *
 * Two columns dividing by the same KIND of denominator -- classified final
 * decisions with a relator, for the person and for the whole bench -- so a
 * reader can see how this docket resolved against how the Tribunal's did.
 * Rows follow the bench's order (largest share first) and include every code
 * either column carries. Both mixes are multi-label, so the shares can sum
 * past 100%; the note is computed from the two numbers the table prints and
 * disappears by itself if the corpus ever becomes single-label. Descriptive
 * only: the guide says why a difference is not a ranking.
 */
function MixComparison({
  person,
  personTotal,
  bench,
  benchTotal,
}: {
  person: CadeLabelCount[] | null;
  personTotal: number;
  bench: CategoryShare[];
  benchTotal: number;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });
  const personByCode = new Map((person ?? []).map((row) => [row.label, row.decisions]));
  const codes = [
    ...bench.map((row) => row.code),
    ...(person ?? []).map((row) => row.label).filter((code) => !bench.some((row) => row.code === code)),
  ];
  const personSum = (person ?? []).reduce((n, row) => n + row.decisions, 0);
  const benchSum = bench.reduce((n, row) => n + row.count, 0);
  const cell = (count: number, total: number) =>
    `${number.format(count)} / ${number.format(total)} (${percent.format(total ? count / total : 0)})`;

  return (
    <div className="space-y-2 text-xs" data-mix-comparison>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('cade:conselheiro.comparison.outcome')}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t('cade:conselheiro.comparison.thisConselheiro', { total: number.format(personTotal) })}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('cade:conselheiro.comparison.bench', { total: number.format(benchTotal) })}
              </th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => {
              const benchRow = bench.find((row) => row.code === code);
              return (
                <tr key={code} className="border-t">
                  <td className="px-3 py-2 text-foreground">
                    {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground" data-person-cell>
                    {person ? cell(personByCode.get(code) ?? 0, personTotal) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground" data-bench-cell>
                    {cell(benchRow?.count ?? 0, benchTotal)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!person ? (
        <p className="text-muted-foreground">{t('cade:conselheiro.comparison.withheld')}</p>
      ) : null}
      {personSum > personTotal || benchSum > benchTotal ? (
        <p className="text-muted-foreground">{t('cade:conselheiro.comparison.multiLabel')}</p>
      ) : null}
    </div>
  );
}

function DecisionList({ items }: { items: CadeConselheiroDecision[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">
        {t('cade:conselheiro.decisions.title', { total: number.format(items.length) })}
      </h2>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('cade:conselheiro.decisions.none')}</p>
      ) : (
        // Bounded, with its own scrollbar. Server-capped at MAX_DECISIONS = 200
        // and the busiest conselheiro carries 40, so this is not about volume
        // today -- it is that an unbounded list pushes the ordering caption and
        // the guide below it off-screen, which is what `/cade/cases` fixed with
        // the same treatment. A labelled region, and deliberately not
        // `tabIndex={0}`: every row carries a case link, so keyboard scrolling
        // already works and a tab stop on the box would only add one to pass
        // through.
        // The region is a wrapping <div>: `role="region"` on the <ul> itself replaced its list
        // role and orphaned every <li> (axe "listitem", serious), the case-page trap again.
        <div
          className="max-h-[60vh] overflow-auto rounded-lg border p-2 text-xs"
          role="region"
          aria-label={t('cade:conselheiro.decisions.listLabel')}
        >
          <ul className="space-y-1.5">
          {items.map((item, index) => (
            <li
              // The index, deliberately. One CASE can be adjudicated in several
              // SESSIONS under the same item number -- 48 groups of decisions
              // share a (case, item) pair, and 08700.003473/2021-16 item 4 is
              // WITHDRAWN in December and CONDEMNED_FINED the following April.
              // The true key is (case, session, item); `session_id` is an agency
              // uuid this product never exposes, and `session_date_raw` still
              // collides on the 3 groups whose session date was never
              // extracted. This list is server-ordered and never reordered
              // client-side, so the index is both stable and collision-free.
              key={index}
              className="flex flex-wrap items-baseline justify-between gap-2 rounded border p-2"
            >
              <span className="space-x-2">
                <Link
                  to={`/cade/c/${item.case_key}`}
                  className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
                >
                  {item.case_number}
                </Link>
                {/* The decision itself -- votes as minuted, fines, text --
                    one click from the row a reader scanning this relator's
                    history stops on. */}
                <Link
                  to={`/cade/votes/d/${encodeURIComponent(item.decision_key)}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {t('cade:case.openDecision')}
                </Link>
                <span className="text-muted-foreground">{item.case_type ?? '—'}</span>
                {/* Which row is the case's last observed decision, and which
                    item on the agenda -- as the case page renders them. The
                    list mixes final and non-final rows, and without the badge
                    a superseded decision reads like the outcome. */}
                {item.item_number ? (
                  <span className="text-muted-foreground">
                    {t('cade:case.item', { item: item.item_number })}
                  </span>
                ) : null}
                {item.is_final_observed ? (
                  <span className="rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-primary">
                    {t('cade:case.final')}
                  </span>
                ) : null}
                {/* What tells two rows on one case apart. Without it the list
                    shows the same case number twice with contradicting
                    outcomes and no way to read which came later. Prose, as
                    `CaseDetailPage` and `DecisionsPage` render it -- this
                    corpus has no parsed date; its years live on the strip. */}
                {item.session_date_raw ? (
                  <span className="text-muted-foreground">{item.session_date_raw}</span>
                ) : null}
              </span>
              <span className="flex shrink-0 items-baseline gap-3">
                <span className="text-muted-foreground">
                  {item.outcome_codes.length === 0
                    ? t('cade:overview.outcomes.UNCLASSIFIED')
                    : item.outcome_codes
                        .map((code) => t(`cade:overview.outcomes.${code}`, code))
                        .join(', ')}
                </span>
                <span className="tabular-nums text-foreground">
                  {item.fine_total_brl === null ? '' : currency.format(item.fine_total_brl)}
                </span>
              </span>
            </li>
          ))}
          </ul>
        </div>
      )}
      {/* `total`, not `count`: i18next reserves `count` as the plural selector and
          takes only a number, so a formatted string there is a type error at best
          and a silently wrong plural at worst. */}
      <p className="text-xs text-muted-foreground">
        {t('cade:conselheiro.decisions.ordering', { total: number.format(items.length) })}
      </p>
    </section>
  );
}

